import React, { type RefObject, useEffect, useState } from 'react';
import { Box } from '@fiverr-private/theme';
import { PauseSolidIcon, PlaySolidIcon, SoundMaxIcon, SoundMutedIcon } from '@fiverr-private/visuals';
import { translate } from '@fiverr-private/i18n-react';
import ControlsIconButton from './ControlsIconButton';

interface CustomControlsProps {
  videoRef: RefObject<HTMLVideoElement>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMuted: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  isPlaying: boolean;
  isMuted?: boolean;
}

const CustomControls = ({ videoRef, setIsMuted, isMuted, isPlaying, setIsPlaying }: CustomControlsProps) => {
  const [percentagePlayed, setPercentagePlayed] = useState(0);
  const mediaElement = videoRef.current;

  useEffect(() => {
    if (mediaElement) {
      const handleTimeUpdate = () => {
        setPercentagePlayed(mediaElement.currentTime / mediaElement.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
      };

      mediaElement.addEventListener('timeupdate', handleTimeUpdate);
      mediaElement.addEventListener('ended', handleEnded);

      return () => {
        mediaElement.removeEventListener('timeupdate', handleTimeUpdate);
        mediaElement.removeEventListener('ended', handleEnded);
      };
    }
  }, [mediaElement, setIsPlaying]);

  const handleMuteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handlePlayClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Box
      height="100%"
      position="absolute"
      width="100%"
      boxSizing="borderBox"
      zIndex="1"
      display="flex"
      alignItems="flexEnd"
    >
      <Box boxSizing="borderBox" width="100%" display="flex" alignItems="center" paddingX="3" paddingBottom="3" gap="2">
        <ControlsIconButton
          onClick={handlePlayClick}
          percentagePlayed={percentagePlayed}
          ariaLabel={translate<string>('media.video.togglePlay')}
        >
          {isPlaying ? <PauseSolidIcon size="sm" color="white" /> : <PlaySolidIcon size="sm" color="white" />}
        </ControlsIconButton>
        <ControlsIconButton onClick={handleMuteClick} ariaLabel={translate<string>('media.video.toggleMute')}>
          {isMuted ? <SoundMutedIcon size="sm" color="white" /> : <SoundMaxIcon size="sm" color="white" />}
        </ControlsIconButton>
      </Box>
    </Box>
  );
};

CustomControls.displayName = 'CustomControls';
export default CustomControls;
